import React from 'react';
import { CircleCheck, KeyRound, Plug, Zap } from 'lucide-react';
import { ParkingSpot } from '../../types/parking';
import { translate } from '../../types/EnumTranslations';
import { isSpotRented, spotHasCharger, spotStatusColor, spotTypeIcon } from '../../Helpers/parkingUtils';
import MaskedIcon from '../../Components/MaskedIcon';

interface ParkingSpotListProps {
    spots: ParkingSpot[];
    selectedSpotKey: string | null;
    /** Drawing key used to flag spots without a marker on the current drawing. */
    currentBlueprintKey?: string;
    onSelect: (spotKey: string) => void;
    /** The row under the mouse, null when it leaves; the page highlights that spot's marker. */
    onHover?: (spotKey: string | null) => void;
}

const ParkingSpotList: React.FC<ParkingSpotListProps> = ({ spots, selectedSpotKey, currentBlueprintKey, onSelect, onHover }) => {
    if (spots.length === 0) {
        return <p style={{ color: '#999', margin: 0 }}>Inga platser registrerade ännu.</p>;
    }

    return (
        <ul className="parking-spot-list">
            {spots.map(spot => {
                const unplaced = !!currentBlueprintKey && !spot.blueprintLocations?.[currentBlueprintKey];
                return (
                    <li
                        key={spot.key}
                        className={`parking-spot-row ${selectedSpotKey === spot.key ? 'parking-spot-row--selected' : ''}`}
                        onClick={() => onSelect(spot.key)}
                        onMouseEnter={() => onHover?.(spot.key)}
                        onMouseLeave={() => onHover?.(null)}
                    >
                        {/* Type icon in the status colour, as on the drawing */}
                        <MaskedIcon src={spotTypeIcon(spot.type)} color={spotStatusColor(spot)} size={22} />
                        <span className="parking-spot-number">{spot.number || '?'}</span>
                        <span className="parking-spot-meta">{translate(spot.type)}</span>
                        {spotHasCharger(spot) && <span className="parking-badge" title="Laddare"><Zap size={12} /></span>}
                        {spot.hasOutlet && <span className="parking-badge" title="Motorvärmaruttag"><Plug size={12} /></span>}
                        {unplaced && <span className="parking-badge parking-badge--warning">Ej placerad</span>}
                        {isSpotRented(spot)
                            ? <span className="parking-badge parking-badge--muted"><KeyRound size={12} aria-hidden="true" />Uthyrd</span>
                            : <span className="parking-badge parking-badge--success"><CircleCheck size={12} aria-hidden="true" />Ledig</span>}
                    </li>
                );
            })}
        </ul>
    );
};

export default ParkingSpotList;
